import { useEffect, useRef, useState } from 'react';
import { Bug, Loader2, AlertTriangle, Camera, Trash2 } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useMe } from '../../hooks/useMe';
import { useCreateFeedback } from '../../hooks/useFeedback';
import ScreenshotAnnotator from '../feedback/ScreenshotAnnotator';
import api from '../../lib/api';
import type { FeedbackType } from '../../types/feedback';
import { Button, Modal } from '../common';

const FEEDBACK_TYPES: { id: FeedbackType; label: string; color: string }[] = [
  { id: 'bug', label: 'Bug', color: '#ef4444' },
  { id: 'feature', label: 'Feature Request', color: '#3b82f6' },
  { id: 'other', label: 'Other', color: '#6b7280' },
];

interface PresignResponse {
  uploadUrl: string;
  downloadUrl: string;
  key: string;
}

export default function FeedbackButton() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="flex-shrink-0 p-2 rounded-lg text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        aria-label="Submit feedback"
        title="Submit feedback"
      >
        <Bug className="w-5 h-5" aria-hidden="true" />
      </button>
      {isOpen && <FeedbackModal onClose={() => setIsOpen(false)} />}
    </>
  );
}

function FeedbackModal({ onClose }: { onClose: () => void }) {
  const location = useLocation();
  const { me } = useMe();
  const createFeedback = useCreateFeedback();

  const authorName =
    [me?.given_name, me?.family_name].filter(Boolean).join(' ').trim() ||
    me?.email ||
    '';

  const [type, setType] = useState<FeedbackType>('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [screenshotBlob, setScreenshotBlob] = useState<Blob | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isAnnotating, setIsAnnotating] = useState(false);
  const [capturedDataUrl, setCapturedDataUrl] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    titleInputRef.current?.focus();
  }, []);

  useEffect(() => {
    return () => {
      if (screenshotPreview) URL.revokeObjectURL(screenshotPreview);
    };
  }, [screenshotPreview]);

  useEffect(() => {
    if (isAnnotating) return;

    function handlePaste(e: ClipboardEvent) {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (!file) continue;

          e.preventDefault();
          const reader = new FileReader();
          reader.onload = () => {
            const dataUrl = reader.result as string;
            setCapturedDataUrl(dataUrl);
            setIsAnnotating(true);
          };
          reader.readAsDataURL(file);
          return;
        }
      }
    }

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [isAnnotating]);

  const handleCapture = async () => {
    setIsCapturing(true);
    // Find any portal-rendered dialog element and hide it during the
    // screen capture so the modal itself doesn't end up in the screenshot.
    const dialog = document.querySelector<HTMLElement>('[role="dialog"]');
    const backdrop = dialog?.closest<HTMLElement>('[role="presentation"]') ?? null;
    const hidden = backdrop ?? dialog;
    const prevDisplay = hidden?.style.display ?? '';
    if (hidden) hidden.style.display = 'none';
    try {
      // Loaded on demand — html2canvas is ~200KB and this button lives in the
      // header of every page, so it must not weigh down the main bundle.
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        scale: 1,
      });
      const dataUrl = canvas.toDataURL('image/png');
      setCapturedDataUrl(dataUrl);
      setIsAnnotating(true);
    } catch {
      setSubmitError('Failed to capture screenshot. You can still submit without one.');
    } finally {
      if (hidden) hidden.style.display = prevDisplay;
      setIsCapturing(false);
    }
  };

  const handleAnnotationDone = (blob: Blob) => {
    setScreenshotBlob(blob);
    setScreenshotPreview(URL.createObjectURL(blob));
    setIsAnnotating(false);
    setCapturedDataUrl(null);
  };

  const handleAnnotationCancel = () => {
    setIsAnnotating(false);
    setCapturedDataUrl(null);
  };

  const handleRemoveScreenshot = () => {
    if (screenshotPreview) URL.revokeObjectURL(screenshotPreview);
    setScreenshotBlob(null);
    setScreenshotPreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!title.trim()) return;

    try {
      let screenshotKey: string | undefined;

      if (screenshotBlob) {
        const presign = await api.post<PresignResponse>('/uploads/presign', {
          filename: `feedback-${Date.now()}.png`,
          contentType: 'image/png',
          entityType: 'feedback',
          entityId: crypto.randomUUID(),
          size: screenshotBlob.size,
        });

        const putResp = await fetch(presign.uploadUrl, {
          method: 'PUT',
          body: screenshotBlob,
          headers: { 'Content-Type': 'image/png' },
        });

        if (!putResp.ok) {
          throw new Error('Screenshot upload failed');
        }

        screenshotKey = presign.key;
      }

      await createFeedback.mutateAsync({
        type,
        title: title.trim(),
        description: description.trim() || undefined,
        page: location.pathname,
        screenshotKey,
      });

      onClose();
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : 'Failed to submit. Try again.'
      );
    }
  };

  const isSubmitting = createFeedback.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      title={isAnnotating ? 'Annotate Screenshot' : 'Submit Feedback'}
      size={isAnnotating ? 'xl' : 'md'}
      dismissOnBackdrop={!isAnnotating}
    >
      {isAnnotating && capturedDataUrl ? (
        <Modal.Body>
          <ScreenshotAnnotator
            imageDataUrl={capturedDataUrl}
            onDone={handleAnnotationDone}
            onCancel={handleAnnotationCancel}
          />
        </Modal.Body>
      ) : (
        <form onSubmit={handleSubmit} className="contents">
          <Modal.Body>
            <div className="flex items-center gap-2 mb-4">
              <Bug className="w-5 h-5 text-red-500" aria-hidden="true" />
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Report a bug or suggest a feature.
              </span>
            </div>
            <div className="space-y-4">
              <div>
                <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Type
                </span>
                <div className="flex flex-wrap gap-2">
                  {FEEDBACK_TYPES.map((t) => {
                    const active = t.id === type;
                    return (
                      <button
                        type="button"
                        key={t.id}
                        onClick={() => setType(t.id)}
                        className="px-3 py-1.5 rounded-full text-xs font-medium border transition-colors"
                        style={{
                          borderColor: t.color,
                          backgroundColor: active ? t.color : 'transparent',
                          color: active ? '#ffffff' : t.color,
                        }}
                        aria-pressed={active}
                      >
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label
                  htmlFor="feedback-title-input"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Title
                </label>
                <input
                  id="feedback-title-input"
                  ref={titleInputRef}
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Brief summary of the issue or idea"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label
                  htmlFor="feedback-description"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Description{' '}
                  <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  id="feedback-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  placeholder="Steps to reproduce, expected behavior, or details about your idea"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Screenshot
                </span>
                {screenshotPreview ? (
                  <div className="relative inline-block">
                    <img
                      src={screenshotPreview}
                      alt="Annotated screenshot"
                      className="max-h-32 rounded border border-gray-300 dark:border-gray-600"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveScreenshot}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                      aria-label="Remove screenshot"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div>
                    <button
                      type="button"
                      onClick={handleCapture}
                      disabled={isCapturing}
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                    >
                      {isCapturing ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Camera className="w-4 h-4" />
                      )}
                      {isCapturing ? 'Capturing...' : 'Capture & Annotate'}
                    </button>
                    <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                      or paste an image (Ctrl+V)
                    </p>
                  </div>
                )}
              </div>

              {submitError && (
                <p className="text-sm text-red-600 dark:text-red-400 inline-flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  {submitError}
                </p>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer align="between">
            <div className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
              {authorName && (
                <p>
                  Reporting as <span className="font-medium">{authorName}</span>
                </p>
              )}
              <p>
                Page: <span className="font-mono">{location.pathname}</span>
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={!title.trim() || isSubmitting}
                leadingIcon={isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : undefined}
              >
                Submit
              </Button>
            </div>
          </Modal.Footer>
        </form>
      )}
    </Modal>
  );
}
